/**
 * Volver tras iniciar sesión (DT-06, PC-24). Antes de mandar al login, los tres caminos
 * (`apiFetch` al fallar el refresh, `handleIdleTimeout` y `AuthGuard`) guardan a dónde
 * volver; `Login` lo recoge al entrar en lugar de ir siempre a `/`.
 *
 * - Solo se guarda una ruta validada con `safeReturnPath` bajo `/accounting` o `/home`.
 *   Si la página actual no lo es pero trae un `?return=` válido (p. ej. Bancos abierto
 *   desde el panel), se guarda ese: es la URL de vuelta que la persona estaba usando.
 * - `sessionStorage` (por pestaña): sobrevive al salto a Cognito y vuelta, y no se
 *   comparte con otras pestañas. La URL de vuelta no lleva la búsqueda (D11).
 * - Se lee una sola vez, caduca a la hora y se borra al cerrar sesión. Si se conocía la
 *   persona al guardarla y entra otra, se descarta.
 *
 * Usa `sessionStorage` directamente (en try/catch) y no `safeSessionStorage`: muchos tests
 * mockean `@/lib/safeStorage` sin ese export, y acceder a él lanzaría en sus árboles.
 */
import { safeReturnPath, RETURN_PARAM } from './returnTo';

export const LOGIN_RETURN_KEY = 'verix.login.return.v1';
export const LOGIN_RETURN_ORIGINS = ['/accounting', '/home'];
const TTL = 60 * 60 * 1000;

function storage() {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Ruta del router (sin el `base` de Vite) de `window.location`. */
function currentRouterLocation() {
  try {
    const { pathname, search } = window.location;
    if (typeof pathname !== 'string') return null;
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
    const path = base && pathname.startsWith(`${base}/`) ? pathname.slice(base.length) : pathname;
    return { pathname: path, search: typeof search === 'string' ? search : '' };
  } catch {
    return null;
  }
}

/** A dónde volver desde `location` (ruta del router), o `null`. */
export function loginReturnTarget(location) {
  if (!location || typeof location.pathname !== 'string') return null;
  const search = location.search || '';
  const here = safeReturnPath(`${location.pathname}${search}`, LOGIN_RETURN_ORIGINS);
  if (here) return here;
  try {
    return safeReturnPath(new URLSearchParams(search).get(RETURN_PARAM), LOGIN_RETURN_ORIGINS);
  } catch {
    return null;
  }
}

/**
 * Guarda a dónde volver antes de ir al login. `location` es la ruta del router; si no se
 * pasa, se lee de `window.location`. En el propio login no hace nada (una segunda
 * redirección no debe pisar la buena); en otra página sin destino válido, borra lo anterior.
 *
 * @param {{ location?: { pathname: string, search?: string } | null, userId?: string | number | null }} [options]
 */
export function rememberLoginReturn({ location, userId = null } = {}) {
  const store = storage();
  const where = location ?? currentRouterLocation();
  if (!store || !where) return;
  if (where.pathname.replace(/\/+$/, '') === '/login') return;
  const path = loginReturnTarget(where);
  try {
    if (!path) store.removeItem(LOGIN_RETURN_KEY);
    else store.setItem(LOGIN_RETURN_KEY, JSON.stringify({ path, userId: userId == null ? null : String(userId), at: Date.now() }));
  } catch { /* storage bloqueado: se vuelve a `/` */ }
}

/**
 * Devuelve (y borra) la ruta guardada, o `null` si no hay, caducó o era de otra persona.
 *
 * @param {{ userId?: string | number | null, now?: number }} [options]
 * @returns {string | null}
 */
export function takeLoginReturn({ userId = null, now = Date.now() } = {}) {
  const store = storage();
  if (!store) return null;
  let saved = null;
  try {
    saved = JSON.parse(store.getItem(LOGIN_RETURN_KEY));
    store.removeItem(LOGIN_RETURN_KEY);
  } catch {
    return null;
  }
  if (!saved || typeof saved !== 'object') return null;
  if (!Number.isFinite(saved.at) || saved.at > now || now - saved.at >= TTL) return null;
  if (saved.userId != null && userId != null && String(userId) !== saved.userId) return null;
  return safeReturnPath(saved.path, LOGIN_RETURN_ORIGINS);
}

export function clearLoginReturn() {
  try {
    storage()?.removeItem(LOGIN_RETURN_KEY);
  } catch { /* storage bloqueado */ }
}
